// Copyright (c) Cosmo Tech.
// Licensed under the MIT license.
import { useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { Api } from '../../../../../services/config/Api';
import { useDatasets } from '../../../../../state/hooks/DatasetHooks';
import { useOrganizationId } from '../../../../../state/hooks/OrganizationHooks';
import {
  useCurrentScenarioId,
  useCurrentScenarioParametersValues,
  useCurrentScenarioDatasetList,
} from '../../../../../state/hooks/ScenarioHooks';
import { useWorkspaceId } from '../../../../../state/hooks/WorkspaceHooks';
import {
  useClasseActifNiveauCoutByGisement,
  useFamilleEquipementByAssetClass,
  useSequenceActionByGisement,
  useTableNamesMappingByGisement,
  useCoutsEtDureesByGisement,
  useImpactsByGisement,
} from '../../../../../state/wizard/WizardHooks';
import { WizardUtils } from '../../../../../utils/WizardUtils';

export const useSequenceActions = (selectedGisement) => {
  const scenarioId = useCurrentScenarioId();
  const sequencesActions = useSequenceActionByGisement(selectedGisement, scenarioId);
  const notSortedNiveauCoutList = useClasseActifNiveauCoutByGisement(selectedGisement, scenarioId);
  const niveauCout = useMemo(() => {
    return WizardUtils.sortResourceListAlphabetically(Array.from(new Set(notSortedNiveauCoutList)));
  }, [notSortedNiveauCoutList]);
  const tableNamesMapping = useTableNamesMappingByGisement(scenarioId, selectedGisement);
  const reduxCoutsEtDurees = useCoutsEtDureesByGisement(selectedGisement, scenarioId);
  const reduxImpacts = useImpactsByGisement(selectedGisement, scenarioId);
  const { getValues } = useFormContext();

  const formCoutsEtDurees = getValues(tableNamesMapping.CoutsEtDureesActions);
  const formImpacts = getValues(tableNamesMapping.Impacts);

  const coutsEtDurees = formCoutsEtDurees ?? reduxCoutsEtDurees ?? [];
  const impacts = formImpacts ?? reduxImpacts ?? [];

  const organizationId = useOrganizationId();
  const workspaceId = useWorkspaceId();
  const datasets = useDatasets();
  const currentScenarioParametersValues = useCurrentScenarioParametersValues();
  const assetClass = tableNamesMapping?.ClasseActif;
  const notSortedFamilleEquipement = useFamilleEquipementByAssetClass(scenarioId, assetClass);
  const familleEquipement = useMemo(() => {
    return [...notSortedFamilleEquipement].sort((a, b) => a.label.localeCompare(b.label));
  }, [notSortedFamilleEquipement]);
  const datasetId = useCurrentScenarioDatasetList()[0];

  const [enveloppesBudgetaire, setEnveloppesBudgetaire] = useState([]);
  const [tachesEnInterventionTable, setTachesEnInterventionTable] = useState([]);

  useEffect(() => {
    const fetchTableData = async (tableName, setStateFunction) => {
      const tableParameterValue = currentScenarioParametersValues.find(
        (parameter) => parameter.parameterId === tableName
      )?.value;
      if (tableParameterValue == null || tableParameterValue === '') {
        const { data } = await Api.Datasets.twingraphQuery(organizationId, datasetId, {
          query: `MATCH (n: ${tableName}) RETURN n`,
        });
        setStateFunction(data.map((node) => node.n.properties) ?? []);
      } else {
        try {
          const data = await WizardUtils.downloadFileData(organizationId, workspaceId, datasets, tableParameterValue);
          setStateFunction(data);
        } catch (error) {
          console.error(`Error downloading file data for ${tableName}:`, error);
          setStateFunction([]);
        }
      }
    };

    fetchTableData('EnveloppesBudgetaires', setEnveloppesBudgetaire);
    fetchTableData('TachesEnIntervention', setTachesEnInterventionTable);
  }, [organizationId, workspaceId, datasets, datasetId, currentScenarioParametersValues]);

  const categoriesBudgetaires = useMemo(() => {
    const categories = [
      ...new Set((enveloppesBudgetaire || []).map((enveloppes) => enveloppes.CategoriesBudgetaires.split('|')).flat()),
    ];
    return WizardUtils.sortResourceListAlphabetically(categories);
  }, [enveloppesBudgetaire]);

  const tachesEnIntervention = useMemo(() => {
    const taches = tachesEnInterventionTable.map((tache) => tache.Identifiant);
    return WizardUtils.sortResourceListAlphabetically(Array.from(new Set(taches)));
  }, [tachesEnInterventionTable]);

  return {
    sequencesActions,
    coutsEtDurees,
    impacts,
    familleEquipement,
    niveauCout,
    tableNamesMapping,
    enveloppesBudgetaire,
    categoriesBudgetaires,
    tachesEnIntervention,
    tachesEnInterventionTable,
  };
};
